import React, { forwardRef, createRef } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import Slider from 'react-slick'
import { useEffect } from 'sb-hooks'
import { checkIsChildNode } from 'common/helpers'

import s from './Carousel.scss'


const defaultOptions = {
  zIndex: 100,
  speed: 300,
  initialSlide: 0,
  slidesToShow: 1,
  slidesToScroll: 1,
  touchThreshold: 5,
  autoplaySpeed: 2000,
  autoplay: false,
  adaptiveHeight: true,
  accessibility: true,
  touchMove: false,
  infinite: false,
  arrows: true,
  swipe: false,
  fade: false,
  dots: false,
  useCSS: true,
  cssEase: 'linear',
}

const Carousel = forwardRef((props, ref) => {
  const { name, className, children, options, afterChange, withAdaptiveHeight } = props

  const containerRef  = createRef()
  const sliderRef     = createRef()

  useEffect(() => {
    let firstClientX
    let firstClientY

    const touchStart = (event) => {
      if (event.touches && event.touches[0]) {
        firstClientX = event.touches[0].clientX
        firstClientY = event.touches[0].clientY
      }
      else {
        firstClientX = event.clientX
        firstClientY = event.clientY
      }
    }

    const touchMove = (event) => {
      const minValue    = 20
      const clientX     = event.touches[0].clientX - firstClientX
      const isChildNode = checkIsChildNode({ node: event.target, parentNode: containerRef.current })

      if (Math.abs(clientX) > minValue && isChildNode && event.cancelable) {
        event.preventDefault()
        event.returnValue = false

        return false
      }
    }

    window.addEventListener('touchstart', touchStart)
    window.addEventListener('touchmove', touchMove, { passive: false })

    if (ref) {
      ref(sliderRef.current)
    }

    // for debug purpose + for stopping autoplay: window['slick-{name}'].slickPause()
    if (name && typeof window !== 'undefined') {
      window[`slick-${name}`] = sliderRef.current
    }

    return () => {
      window.removeEventListener('touchstart', touchStart)
      window.removeEventListener('touchmove', touchMove, { passive: false })
    }
  }, [ ref ])

  const mergedOptions = { ...defaultOptions, ...options }

  const rootClassName = cx(className, s.carousel, {
    [s.withArrows]: mergedOptions.arrows,
    [s.withAdaptiveHeight]: withAdaptiveHeight,
  })

  return (
    <div className={rootClassName} ref={containerRef}>
      <Slider
        afterChange={afterChange}
        {...mergedOptions}
        ref={sliderRef}
      >
        {children}
      </Slider>
    </div>
  )
})

Carousel.propTypes = {
  name: PropTypes.string,
  className: PropTypes.string,
  options: PropTypes.shape({
    slidesToShow: PropTypes.number,
    slidesToScroll: PropTypes.number,
    arrows: PropTypes.bool,
    dots: PropTypes.bool,
    infinite: PropTypes.bool,
    speed: PropTypes.number,
    centerMode: PropTypes.bool,
    centerPadding: PropTypes.string,
    adaptiveHeight: PropTypes.bool,
    lazyLoad: PropTypes.string,
    autoplay: PropTypes.bool,
    autoplaySpeed: PropTypes.number,
    fade: PropTypes.bool,
    cssEase: PropTypes.string,
    touchMove: PropTypes.bool,
    touchThreshold: PropTypes.number,
    vertical: PropTypes.bool,
    verticalSwiping: PropTypes.bool,
    zIndex: PropTypes.number,
    accessibility: PropTypes.bool,
    dotsClass: PropTypes.string,
  }),
  children: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
  ]),
  afterChange: PropTypes.func,
  onSwipe: PropTypes.func,
  withAdaptiveHeight: PropTypes.bool,
}


export default Carousel
